// @flow

import * as React from 'react';
import {useState} from 'react';
import Box from 'sail/Box';
import {ButtonLink} from 'sail/Button';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
} from 'sail/DataTable';
import {InlineLink} from 'sail/InlineLink';
import {BodyMono, Subheading} from 'sail/Text';
import {Badge} from 'docs/components';
import type {
  ContextDocumentation,
  ContextPropertyDocumentation,
} from 'docs/types';
import './RegisteredContextsTable.scss';

type ContextOwnerProps = {|
  owner: ?string,
|};

const ContextOwner = ({owner}: ContextOwnerProps) => {
  if (!owner) {
    return <Badge color="yellow" icon="warning" label="Unknown" />;
  }
  return <Badge color="gray" label={owner} />;
};

type ContextNameProps = {|
  name: string,
  filename: ?string,
|};

const ContextName = ({name, filename}: ContextNameProps) => {
  if (!filename) {
    return name;
  }

  const liveGrepUrl = `https://livegrep.corp.stripe.com/view/stripe-internal/pay-server${filename}`;
  return (
    <InlineLink href={liveGrepUrl} target="_blank">
      {name}
    </InlineLink>
  );
};

type ContextPropertiesProps = {|
  properties: Array<ContextPropertyDocumentation>,
|};

const ContextProperties = ({properties}: ContextPropertiesProps) => (
  <React.Fragment>
    <DataTableRow background="offset">
      <DataTableCell />
      <DataTableCell>
        <Subheading>Property Name</Subheading>
      </DataTableCell>
      <DataTableCell>
        <Subheading>Default Value</Subheading>
      </DataTableCell>
      <DataTableCell>
        <Subheading>Sorbet Type</Subheading>
      </DataTableCell>
    </DataTableRow>
    {properties.map((prop) => (
      <DataTableRow key={prop.name} background="offset">
        <DataTableCell />
        <DataTableCell>
          <BodyMono>{prop.name}</BodyMono>
        </DataTableCell>
        <DataTableCell>
          <BodyMono>{prop.default_value}</BodyMono>
        </DataTableCell>
        <DataTableCell>
          <BodyMono>{prop.type}</BodyMono>
        </DataTableCell>
      </DataTableRow>
    ))}
  </React.Fragment>
);

type ContextRowGroupProps = {|
  name: string,
  context: ContextDocumentation,
|};

const ContextRowGroup = ({name, context}: ContextRowGroupProps) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    setExpanded((current) => !current);
  };

  return (
    <React.Fragment>
      <DataTableRow className="RegisteredContextsTable-ContextRowGroup">
        <DataTableCell>
          <ButtonLink
            color="gray"
            icon={expanded ? 'chevronUp' : 'chevronDown'}
            label="Toggle properties"
            hideLabel
            onClick={handleToggle}
          />
        </DataTableCell>
        <DataTableCell width={200}>
          <BodyMono>
            <ContextName name={name} filename={context.source_filename} />
          </BodyMono>
        </DataTableCell>
        <DataTableCell width={150}>
          <ContextOwner owner={context.owner} />
        </DataTableCell>
        <DataTableCell>{context.description}</DataTableCell>
      </DataTableRow>
      {expanded && <ContextProperties properties={context.properties} />}
    </React.Fragment>
  );
};

type RegisteredContextsTableProps = {|
  contexts: {[name: string]: ContextDocumentation},
|};

export const RegisteredContextsTable = ({
  contexts,
}: RegisteredContextsTableProps) => (
  <Box padding={{top: 12}}>
    <DataTable className="RegisteredContextsTable" size="medium">
      <DataTableHead
        columns={[
          {},
          {label: 'Name'},
          {label: 'Owner'},
          {label: 'Description'},
        ]}
      />
      <DataTableBody>
        {Object.keys(contexts).map((name) => (
          <ContextRowGroup key={name} name={name} context={contexts[name]} />
        ))}
      </DataTableBody>
    </DataTable>
  </Box>
);
